/**
 * Max Wealth Dashboard Overlay — unified single-panel design
 * Merges signal info + targets into one compact card.
 * Toggle support, arrow badges, green on buy / red on sell.
 * CSS prefix: mw-
 */
import React from "react";
import { MaxWealthDashboard } from "../../indicators/built-in/max-wealth";
import "./MaxWealthOverlay.scss";

interface MaxWealthOverlayProps {
  dashboard: MaxWealthDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const MaxWealthOverlay: React.FC<MaxWealthOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const { signal, trend, preset, entry, stopLoss, trailingStop,
    target1, target2, target3, currentPrice, livePL, bookedPL,
    status, tp1Hit, tp2Hit, tp3Hit, slHit, tslHit } = dashboard;

  const isLong = trend === "LONG";
  const isShort = trend === "SHORT";
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const statusText =
    status === "Target1 Hit" ? "TGT-1 Hit"
    : status === "Target2 Hit" ? "TGT-2 Hit"
    : status === "Target3 Hit" ? "TGT-3 Hit"
    : status === "Stop Loss Hit" ? "SL Hit"
    : status === "Trailing SL Hit" ? "TSL Hit"
    : "Active";

  const statusBarMod =
    status === "Active" ? ""
    : status.includes("Target") ? " mw-status-bar--tgt"
    : status === "Stop Loss Hit" ? " mw-status-bar--sl"
    : " mw-status-bar--tsl";

  const themeClass = isLong ? "mw-buy" : isShort ? "mw-sell" : "mw-neutral";

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`mw-arrow-badge mw-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

      <div className={`mw-overlay ${themeClass}${showDashboard ? "" : " mw-overlay--collapsed"}`}>
        {/* Title row with toggle */}
        <div className="mw-title-row">
          <span className="mw-title">Max Wealth</span>
          <button
            className="mw-toggle-btn"
            onClick={onToggleDashboard}
            title={showDashboard ? "Hide dashboard" : "Show dashboard"}
            aria-label={showDashboard ? "Hide dashboard" : "Show dashboard"}
          >
            <span className={`mw-toggle-icon${showDashboard ? " mw-toggle-icon--open" : ""}`}>&#x25BE;</span>
          </button>
        </div>

        {showDashboard && (
          <>
            {/* Symbol + Price row */}
            <div className="mw-header-row">
              <span className="mw-symbol">{symbol}</span>
              <span className="mw-cmp">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Entry row */}
            <div className="mw-entry-row">
              <span className="mw-entry-label">Entry:</span>
              <span className="mw-entry-value">{fmt(entry)}</span>
            </div>

            {/* P&L row */}
            <div className="mw-pl-row">
              <div className="mw-pl-cell">
                <span className={`mw-pl-val ${bookedPL >= 0 ? "mw-profit" : "mw-loss"}`}>{fmtPL(bookedPL)}</span>
                <span className="mw-pl-lbl">Booked P&amp;L</span>
              </div>
              <div className="mw-pl-cell">
                <span className={`mw-pl-val ${livePL >= 0 ? "mw-profit" : "mw-loss"}`}>{fmtPL(livePL)}</span>
                <span className="mw-pl-lbl">Live P&amp;L</span>
              </div>
            </div>

            {/* Target / SL section */}
            <div className="mw-targets">
              <div className="mw-tgt-row">
                <span className="mw-tgt-label">Target_1</span>
                <span className="mw-tgt-sep">:</span>
                <span className={`mw-tgt-value ${tp1Hit ? "mw-tgt-hit" : ""}`}>{fmt(target1)}{tp1Hit ? " ✓" : ""}</span>
              </div>
              <div className="mw-tgt-row">
                <span className="mw-tgt-label">Target_2</span>
                <span className="mw-tgt-sep">:</span>
                <span className={`mw-tgt-value ${tp2Hit ? "mw-tgt-hit" : ""}`}>{fmt(target2)}{tp2Hit ? " ✓" : ""}</span>
              </div>
              <div className="mw-tgt-row">
                <span className="mw-tgt-label">Target_3</span>
                <span className="mw-tgt-sep">:</span>
                <span className={`mw-tgt-value ${tp3Hit ? "mw-tgt-hit" : ""}`}>{fmt(target3)}{tp3Hit ? " ✓" : ""}</span>
              </div>
              <div className="mw-tgt-row">
                <span className="mw-tgt-label">Stop Loss</span>
                <span className="mw-tgt-sep">:</span>
                <span className={`mw-tgt-value mw-sl-val ${slHit ? "mw-sl-hit" : ""}`}>{fmt(stopLoss)}{slHit ? " ✓" : ""}</span>
              </div>
              <div className="mw-tgt-row">
                <span className="mw-tgt-label">Moving SL</span>
                <span className="mw-tgt-sep">:</span>
                <span className={`mw-tgt-value ${tslHit ? "mw-tsl-hit" : ""}`}>{fmt(trailingStop)}{tslHit ? " ✓" : ""}</span>
              </div>
            </div>

            {/* Status bar */}
            <div className={`mw-status-bar${statusBarMod}`}>
              <span className="mw-status-text">{statusText}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MaxWealthOverlay;
